import React, { useState } from "react";
import { 
  Container, 
  Typography, 
  Box, 
  Stack, 
  Button,
  Paper,
  Grid,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar
} from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedIcon from '@mui/icons-material/Verified';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SellIcon from '@mui/icons-material/Sell';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageIcon from '@mui/icons-material/Image';

const steps = [
  {
    label: 'Submit Coin Details',
    description: 'Provide comprehensive coin information including high-resolution images, year, mint mark, metal type, and any known provenance.',
    icon: <DescriptionIcon />,
  },
  {
    label: 'Expert Evaluation',
    description: 'Our numismatists verify authenticity, assess condition, grade the coin, and determine current market value.',
    icon: <AssessmentIcon />,
  },
  {
    label: 'Offer & Escrow Agreement',
    description: 'Receive a competitive, transparent offer and agree to secure payment terms through our institutional escrow service.',
    icon: <VerifiedIcon />,
  },
  {
    label: 'Secure Payment',
    description: 'Get paid quickly and safely via our protected escrow system with full insurance coverage.',
    icon: <PaymentIcon />,
  },
];

const SellCoins = () => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [sellStep, setSellStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [coinImages, setCoinImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [formData, setFormData] = useState({
    // Coin Details
    coinName: '',
    year: '',
    mint: '',
    metal: '',
    weight: '',
    grade: '',
    certificationNumber: '',
    quantity: '1',
    // Personal Details
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    // Additional
    expectedPrice: '',
    notes: '',
    agreeToTerms: false
  });

  const metals = ['Gold', 'Silver', 'Platinum', 'Palladium', 'Copper', 'Bronze'];
  const grades = ['MS70', 'MS69', 'MS68', 'MS67', 'MS66', 'MS65', 'MS64', 'MS63', 
                  'MS62', 'MS61', 'MS60', 'AU58', 'AU55', 'AU53', 'AU50', 
                  'XF45', 'XF40', 'VF35', 'VF30', 'VF25', 'VF20'];

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = [...coinImages];
    const newPreviews = [...previewImages];
    
    files.forEach(file => {
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSnackbarMessage('Image size should be less than 10MB');
        setSnackbarOpen(true);
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setSnackbarMessage('Please upload only image files');
        setSnackbarOpen(true);
        return;
      }
      
      newImages.push(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        setPreviewImages([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });
    
    setCoinImages(newImages);
  };

  const handleRemoveImage = (index) => {
    const newImages = coinImages.filter((_, i) => i !== index);
    const newPreviews = previewImages.filter((_, i) => i !== index);
    setCoinImages(newImages);
    setPreviewImages(newPreviews);
  };

  const handleSubmitSellRequest = async () => {
    // Prepare email content with images info
    const subject = `Sell Request: ${formData.coinName || 'Coin Collection'}`;
    const body = `
      Sell Request Details:
      
      Coin Information:
      - Coin Name: ${formData.coinName}
      - Year: ${formData.year}
      - Mint: ${formData.mint}
      - Metal: ${formData.metal}
      - Weight: ${formData.weight}
      - Grade: ${formData.grade}
      - Certification #: ${formData.certificationNumber}
      - Quantity: ${formData.quantity}
      - Expected Price: ${formData.expectedPrice}
      - Number of Images: ${coinImages.length}
      
      Seller Information:
      - Full Name: ${formData.fullName}
      - Email: ${formData.email}
      - Phone: ${formData.phone}
      - Address: ${formData.address}
      - City: ${formData.city}
      - Country: ${formData.country}
      - Postal Code: ${formData.postalCode}
      
      Additional Notes:
      ${formData.notes}
    `;

    // For actual implementation, you would upload images to a server here
    // For now, we'll simulate image attachment in email
    if (coinImages.length > 0) {
      setSnackbarMessage(`Sell request with ${coinImages.length} image(s) prepared! Please check your email client.`);
    } else {
      setSnackbarMessage('Sell request sent! Our team will contact you within 24 hours.');
    }
    
    // Create mailto link (note: images won't be attached via mailto)
    const mailtoLink = `mailto:john.asfar@worldwidecorporation.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open default email client
    window.location.href = mailtoLink;
    
    setSnackbarOpen(true);
    
    // Close dialog after 2 seconds
    setTimeout(() => {
      setSellDialogOpen(false);
      setSellStep(0);
      setFormData({
        coinName: '',
        year: '',
        mint: '',
        metal: '',
        weight: '',
        grade: '',
        certificationNumber: '',
        quantity: '1',
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        postalCode: '',
        expectedPrice: '',
        notes: '',
        agreeToTerms: false
      });
      setCoinImages([]);
      setPreviewImages([]);
    }, 2000);
  };

  const sellSteps = ['Coin Details', 'Your Information', 'Review & Submit'];

  const getSellStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D', mb: 3 }}>
              Tell us about your coin
            </Typography>
            
            {/* Image Upload Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                Coin Images
              </Typography>
              <Typography variant="caption" color="#616161" sx={{ display: 'block', mb: 2 }}>
                Upload high-quality images of your coin (front, back, and any details)
              </Typography>
              
              {/* Image Upload Button */}
              <Button
                variant="outlined"
                component="label"
                startIcon={<AddPhotoAlternateIcon />}
                sx={{ mb: 2 }}
              >
                Upload Images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
              
              {/* Image Preview Grid */}
              {previewImages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="#616161" sx={{ mb: 1 }}>
                    {previewImages.length} image(s) uploaded
                  </Typography>
                  <ImageList sx={{ width: '100%', height: 'auto' }} cols={3} rowHeight={120}>
                    {previewImages.map((image, index) => (
                      <ImageListItem key={index} sx={{ position: 'relative' }}>
                        <img
                          src={image}
                          alt={`Coin preview ${index + 1}`}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                        <ImageListItemBar
                          sx={{
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                          }}
                          title={`Image ${index + 1}`}
                          actionIcon={
                            <IconButton
                              sx={{ color: 'white' }}
                              onClick={() => handleRemoveImage(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                          actionPosition="right"
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
              
              {/* No Images Message */}
              {previewImages.length === 0 && (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: alpha('#C6A75E', 0.05),
                    border: '2px dashed',
                    borderColor: alpha('#C6A75E', 0.3),
                    mb: 3
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: '#9E9E9E', mb: 1 }} />
                  <Typography variant="body2" color="#616161">
                    No images uploaded yet. Click the button above to add photos of your coin.
                  </Typography>
                  <Typography variant="caption" color="#9E9E9E">
                    Clear photos help us provide a more accurate valuation
                  </Typography>
                </Paper>
              )}
            </Box>
            
            <TextField
              fullWidth
              label="Coin Name/Description"
              name="coinName"
              value={formData.coinName}
              onChange={handleFormChange}
              margin="normal"
              required
              helperText="Include denomination, design, and any notable features"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Year"
                  name="year"
                  value={formData.year}
                  onChange={handleFormChange}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Mint"
                  name="mint"
                  value={formData.mint}
                  onChange={handleFormChange}
                  margin="normal"
                  placeholder="e.g., Philadelphia, Perth, Royal Mint"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Metal Type</InputLabel>
                  <Select
                    name="metal"
                    value={formData.metal}
                    onChange={handleFormChange}
                    label="Metal Type"
                  >
                    {metals.map(metal => (
                      <MenuItem key={metal} value={metal}>{metal}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Weight (grams/oz)"
                  name="weight"
                  value={formData.weight}
                  onChange={handleFormChange}
                  margin="normal"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Grade (if known)</InputLabel>
                  <Select
                    name="grade"
                    value={formData.grade}
                    onChange={handleFormChange}
                    label="Grade (if known)"
                  >
                    <MenuItem value="">Unknown/Not Graded</MenuItem>
                    {grades.map(grade => (
                      <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Certification #"
                  name="certificationNumber"
                  value={formData.certificationNumber}
                  onChange={handleFormChange}
                  margin="normal"
                  placeholder="PCGS/NGC #"
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{ inputProps: { min: 1 } }}
            />
            <TextField
              fullWidth
              label="Expected Price (Optional)"
              name="expectedPrice"
              value={formData.expectedPrice}
              onChange={handleFormChange}
              margin="normal"
              InputProps={{
                startAdornment: <AttachMoneyIcon sx={{ color: 'primary.main', mr: 1 }} />,
              }}
              helperText="Your estimated value for the coin(s)"
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D', mb: 3 }}>
              Your Contact Information
            </Typography>
            <TextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleFormChange}
              margin="normal"
              required
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleFormChange}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleFormChange}
                  margin="normal"
                  required
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleFormChange}
              margin="normal"
              required
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D' }}>
              Ready to Submit?
            </Typography>
            <Typography variant="body2" color="#616161" sx={{ mb: 3 }}>
              By submitting this form, you agree to our terms and conditions. 
              Our numismatic experts will review your coin details and contact you within 24-48 hours.
            </Typography>
            
            {/* Image Summary */}
            {previewImages.length > 0 && (
              <Paper sx={{ p: 2, backgroundColor: alpha('#C6A75E', 0.05), mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D' }}>
                  Images Uploaded: {previewImages.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {previewImages.slice(0, 4).map((image, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={image}
                      alt={`Coin ${index + 1}`}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: alpha('#C6A75E', 0.3)
                      }}
                    />
                  ))}
                  {previewImages.length > 4 && (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 1,
                        backgroundColor: alpha('#C6A75E', 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: alpha('#C6A75E', 0.3)
                      }}
                    >
                      <Typography variant="caption" color="primary">
                        +{previewImages.length - 4}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
            
            <Paper sx={{ p: 2, backgroundColor: alpha('#C6A75E', 0.05), mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D' }}>
                Summary
              </Typography>
              <Typography variant="body2" color="#616161">
                {formData.coinName || 'Coin'} - {formData.quantity} piece(s)
              </Typography>
              {formData.expectedPrice && (
                <Typography variant="body2" color="#616161">
                  Expected Value: ${formData.expectedPrice}
                </Typography>
              )}
            </Paper>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  const handleSellNext = () => {
    // Validation for step 0 - require at least one image
    if (sellStep === 0 && coinImages.length === 0) {
      setSnackbarMessage('Please upload at least one image of your coin');
      setSnackbarOpen(true);
      return;
    }
    setSellStep((prevStep) => prevStep + 1);
  };

  const handleSellBack = () => {
    setSellStep((prevStep) => prevStep - 1);
  };

  return (
    <Box sx={{ 
      py: 8,
      backgroundColor: '#F5F5F5',
      minHeight: '100vh',
    }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography variant="h2" gutterBottom align="center" sx={{ color: '#2D2D2D' }}>
            Turn Your Collection
            <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
              into Capital
            </Box>
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            color="#616161" 
            sx={{ maxWidth: '700px', mx: 'auto', mb: 6 }}
          >
            Our streamlined process ensures a secure, transparent, and profitable experience 
            for selling your rare coins and collections.
          </Typography>
        </motion.div>

        <Grid container spacing={6}>
          {/* Left Column - Process */}
          <Grid item xs={12} md={7}>
            <Paper
              sx={{
                p: 4,
                backgroundColor: '#FFFFFF',
                border: '1px solid',
                borderColor: alpha('#9E9E9E', 0.15),
                boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
              }}
            >
              <Typography variant="h4" gutterBottom sx={{ color: '#2D2D2D', mb: 4 }}>
                Our Process
              </Typography>

              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 0,
                            backgroundColor: index <= activeStep ? 'primary.main' : alpha('#9E9E9E', 0.1),
                            color: index <= activeStep ? '#FFFFFF' : '#9E9E9E',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                          }}
                        >
                          {step.icon}
                        </Box>
                      )}
                    >
                      <Typography variant="h6" sx={{ color: '#2D2D2D' }}>
                        {step.label}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="#616161" sx={{ mb: 2, ml: 7 }}>
                        {step.description}
                      </Typography>
                      <Box sx={{ mb: 2, ml: 7 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setActiveStep(index + 1)}
                          sx={{
                            borderColor: alpha('#9E9E9E', 0.3),
                            color: '#616161',
                            '&:hover': {
                              borderColor: 'primary.main',
                              color: 'primary.main',
                            },
                          }}
                        >
                          Continue
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {activeStep === steps.length && (
                <Paper
                  square
                  elevation={0}
                  sx={{
                    p: 3,
                    mt: 2,
                    backgroundColor: alpha('#C6A75E', 0.05),
                    border: '1px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography sx={{ color: '#2D2D2D' }}>
                    Your coins are ready for evaluation! Our team will contact you within 24 hours.
                  </Typography>
                </Paper>
              )}
            </Paper>
          </Grid>

          {/* Right Column - Benefits & CTA */}
          <Grid item xs={12} md={5}>
            <Stack spacing={4}>
              {/* Benefits */}
              <Paper
                sx={{
                  p: 4,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid',
                  borderColor: alpha('#9E9E9E', 0.15),
                  boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ color: '#2D2D2D', mb: 3 }}>
                  Why Sell With Us
                </Typography>
                
                <Stack spacing={2}>
                  {[
                    'Competitive market-based offers',
                    'Free professional authentication',
                    'Secure escrow protection',
                    'Global buyer network',
                    'Fast, transparent process',
                    'White-glove service',
                  ].map((benefit, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          backgroundColor: 'primary.main',
                          transform: 'rotate(45deg)',
                        }}
                      />
                      <Typography variant="body2" color="#616161">
                        {benefit}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>

              {/* CTA */}
              <Paper
                sx={{
                  p: 4,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ color: '#2D2D2D' }}>
                  Ready to Sell?
                </Typography>
                <Typography variant="body2" color="#616161" sx={{ mb: 3 }}>
                  Get a preliminary valuation within 48 hours
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setSellDialogOpen(true)}
                  endIcon={<SellIcon />}
                  sx={{ py: 2 }}
                >
                  Start Selling Now
                </Button>
                <Typography variant="caption" color="#9E9E9E" sx={{ display: 'block', mt: 2 }}>
                  No obligation • Strictly confidential
                </Typography>
              </Paper>

              {/* Trust Badge */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="#9E9E9E">
                  Member of: PCGS • NGC • ICTA • PNG
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {/* Bottom Section - Testimonials */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ color: '#2D2D2D' }}>
            Trusted by Sellers Worldwide
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              {
                quote: "Sold my collection for 20% above estimate. The process was seamless and professional.",
                author: "Richard H.",
                location: "Private Collector, Chicago",
              },
              {
                quote: "Their expertise and network helped me achieve exceptional value for my inherited coins.",
                author: "Patricia M.",
                location: "Estate Seller, London",
              },
            ].map((testimonial, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  sx={{
                    p: 4,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#9E9E9E', 0.15),
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2, color: '#2D2D2D', fontStyle: 'italic' }}>
                    "{testimonial.quote}"
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: '#2D2D2D' }}>
                    {testimonial.author}
                  </Typography>
                  <Typography variant="caption" color="#9E9E9E">
                    {testimonial.location}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* Sell Dialog */}
      <Dialog 
        open={sellDialogOpen} 
        onClose={() => setSellDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Typography variant="h4" color="primary" gutterBottom>
            Sell Your Coins
          </Typography>
          <Stepper activeStep={sellStep} sx={{ mt: 2 }}>
            {sellSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent>
          {getSellStepContent(sellStep)}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button
            onClick={() => setSellDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Box>
            {sellStep > 0 && (
              <Button
                onClick={handleSellBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
            )}
            {sellStep === sellSteps.length - 1 ? (
              <Button
                onClick={handleSubmitSellRequest}
                variant="contained"
                startIcon={<SellIcon />}
              >
                Submit Request
              </Button>
            ) : (
              <Button
                onClick={handleSellNext}
                variant="contained"
              >
                Next
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SellCoins;